import React, { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import SEO from "../components/SEO.jsx"
import "./GoogleAds.css"

const initialForm = {
  name: "",
  business: "",
  email: "",
  phone: "",
  website: "",
  monthlyBudget: "",
  message: "",
}

const budgetOptions = [
  "Not sure yet",
  "$500–$1,000 / month",
  "$1,000–$2,000 / month",
  "$2,000–$4,000 / month",
  "$4,000+ / month",
]

const faqs = [
  {
    question: "How much should I expect to spend on Google Ads?",
    answer:
      "Most local service businesses start between $15 and $50 per day. Your Google budget is separate from our management fee.",
  },
  {
    question: "How quickly will I see results?",
    answer:
      "Most campaigns need 2 to 4 weeks of data before clear patterns appear. Month one is learning. Month two is refinement. Month three is where consistency usually starts to improve.",
  },
  {
    question: "Do I need a new website first?",
    answer:
      "Not always. If your current site has a clear service, a strong call to action, and a working enquiry form, we can often start there. If not, we can build a focused landing page first.",
  },
  {
    question: "What type of businesses is this best for?",
    answer:
      "Google Ads works best for businesses with clear search intent. That includes services people actively search for when they need help now or soon.",
  },
]

function normaliseWebsite(url) {
  if (!url.trim()) return ""
  if (/^https?:\/\//i.test(url.trim())) return url.trim()
  return `https://${url.trim()}`
}

export default function GoogleAds() {
  const [form, setForm] = useState(initialForm)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [openFaq, setOpenFaq] = useState(0)

  const currentYear = useMemo(() => new Date().getFullYear(), [])

  const handleChange = (event) => {
    const { name, value } = event.target
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setSubmitting(true)
    setError("")

    const payload = {
      name: form.name,
      business: form.business,
      email: form.email,
      phone: form.phone,
      website: normaliseWebsite(form.website),
      monthlyBudget: form.monthlyBudget,
      message: form.message,
      source: "Google Ads Page",
    }

    try {
      const response = await fetch("https://forms.2merative.com/api/2merative-contact.php?form=google-ads", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error("Form submission failed")
      }

      setSubmitted(true)
      setForm(initialForm)
    } catch (err) {
      setError("There was a problem sending your request. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  const scrollToForm = () => {
    const formSection = document.getElementById("google-ads-enquiry")
    if (formSection) {
      formSection.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  return (
    <main className="google-ads-page">
      <SEO
        title="Google Ads Adelaide | Local Lead Generation Campaigns | 2merative"
        description="Google Ads Adelaide services for local businesses that want better leads, clearer tracking, and a stronger path from search to enquiry."
        path="/google-ads-adelaide"
        image="/og-image.png"
      />

      <section className="google-ads-hero">
        <div className="container google-ads-hero__grid">
          <div className="google-ads-hero__content">
            <p className="google-ads-hero__eyebrow">Google Ads for Adelaide businesses</p>

            <h1 className="google-ads-hero__title">
              Turn Google searches into
              <span> real enquiries</span>
            </h1>

            <p className="google-ads-hero__body">
              We build focused Google Ads campaigns designed to bring in genuine leads,
              not empty clicks. Clear targeting. Better landing pages. A smarter path from
              search to enquiry.
            </p>

            <p className="google-ads-hero__body">
              If you also need stronger conversion pages, explore our{" "}
              <Link to="/website-landing">website packages</Link>, learn more about{" "}
              <Link to="/web-design-adelaide">web design in Adelaide</Link>, or{" "}
              <Link to="/contact">get in touch</Link>.
            </p>

            <div className="google-ads-hero__actions">
              <button type="button" className="btn btn-primary" onClick={scrollToForm}>
                Get a Free Ads Review
              </button>
              <a className="btn btn-ghost" href="#google-ads-pricing">
                View Pricing
              </a>
            </div>

            <ul className="google-ads-hero__proof">
              <li>Focused local search campaigns</li>
              <li>Clear conversion tracking setup</li>
              <li>Landing page guidance included</li>
            </ul>
          </div>

          <aside className="google-ads-hero__card" aria-label="Google Ads snapshot">
            <div className="google-ads-stat">
              <span className="google-ads-stat__label">Best for</span>
              <strong className="google-ads-stat__value">Local service businesses</strong>
            </div>

            <div className="google-ads-stat">
              <span className="google-ads-stat__label">Starting ad budget</span>
              <strong className="google-ads-stat__value">$15–$50/day</strong>
            </div>

            <div className="google-ads-stat">
              <span className="google-ads-stat__label">Minimum campaign phase</span>
              <strong className="google-ads-stat__value">2–3 months</strong>
            </div>

            <div className="google-ads-stat">
              <span className="google-ads-stat__label">What matters most</span>
              <strong className="google-ads-stat__value">
                Search intent + landing page
              </strong>
            </div>
          </aside>
        </div>
      </section>

      <section className="google-ads-section google-ads-section--light">
        <div className="container">
          <div className="google-ads-section__intro">
            <p className="section-kicker">The problem</p>
            <h2>Most businesses waste money before they realise what is wrong</h2>
            <p>
              Google Ads can work extremely well, but only when the whole path is right.
              Poor targeting, weak ad copy, or sending traffic to the wrong page burns
              budget fast.
            </p>
          </div>

          <div className="google-ads-problems">
            <article className="google-ads-problem-card">
              <h3>Clicks but no enquiries</h3>
              <p>
                Traffic means very little if visitors are not taking action. We focus on
                intent, relevance, and clear calls to action.
              </p>
            </article>

            <article className="google-ads-problem-card">
              <h3>Budget goes too fast</h3>
              <p>
                Loose keywords and poor exclusions waste spend. We keep campaigns tighter
                and more deliberate from the start.
              </p>
            </article>

            <article className="google-ads-problem-card">
              <h3>No clear tracking</h3>
              <p>
                If form submissions and leads are not being tracked properly, there is no
                reliable way to improve performance.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="google-ads-section">
        <div className="container">
          <div className="google-ads-section__intro">
            <p className="section-kicker">What we actually do</p>
            <h2>A practical lead generation system, not just ads</h2>
            <p>
              We focus on the key parts that turn search traffic into enquiries.
            </p>
          </div>

          <div className="google-ads-features">
            <article className="google-ads-feature-card">
              <div className="google-ads-feature-card__number">01</div>
              <h3>Target the right searches</h3>
              <p>
                We build campaigns around high-intent keywords people use when they are
                ready to act, not broad terms that attract the wrong traffic.
              </p>
            </article>

            <article className="google-ads-feature-card">
              <div className="google-ads-feature-card__number">02</div>
              <h3>Write ads that earn the click</h3>
              <p>
                Strong headlines, clear offers, and useful ad extensions help the right
                people choose your business over the next option.
              </p>
            </article>

            <article className="google-ads-feature-card">
              <div className="google-ads-feature-card__number">03</div>
              <h3>Send traffic to a page that converts</h3>
              <p>
                If your landing experience is weak, Google Ads becomes expensive. We make
                sure the page supports the click and pushes toward enquiry.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="google-ads-section google-ads-section--alt">
        <div className="container">
          <div className="google-ads-section__intro">
            <p className="section-kicker">How it works</p>
            <h2>Simple process. Clear decisions.</h2>
          </div>

          <div className="google-ads-steps">
            <article className="google-ads-step">
              <span className="google-ads-step__index">Step 1</span>
              <h3>Setup</h3>
              <p>
                We review your offer, your current website, and whether we should use your
                existing page or build a focused landing page.
              </p>
            </article>

            <article className="google-ads-step">
              <span className="google-ads-step__index">Step 2</span>
              <h3>Launch</h3>
              <p>
                We build the campaign, add tracking, write the ads, and launch with a
                focused keyword set built around real buying intent.
              </p>
            </article>

            <article className="google-ads-step">
              <span className="google-ads-step__index">Step 3</span>
              <h3>Optimise</h3>
              <p>
                We review search terms, add negative keywords, improve ads, and refine
                performance based on real data, not guesswork.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section id="google-ads-pricing" className="google-ads-section">
        <div className="container">
          <div className="google-ads-section__intro">
            <p className="section-kicker">Pricing</p>
            <h2>Two costs. Your management fee and your Google spend.</h2>
            <p>
              Your ad budget is paid directly to Google. Our fee covers setup, management,
              optimisation, and advice on the landing experience.
            </p>
          </div>

          <div className="google-ads-pricing">
            <article className="google-ads-pricing-card google-ads-pricing-card--featured">
              <div className="google-ads-pricing-card__badge">Most likely fit</div>
              <h3>Starter</h3>
              <p className="google-ads-pricing-card__price">From $300/month</p>
              <p className="google-ads-pricing-card__setup">Setup from $600</p>

              <ul>
                <li>1 focused Search campaign</li>
                <li>1–2 ad groups</li>
                <li>High-intent keyword setup</li>
                <li>Negative keyword base list</li>
                <li>Ad copy + extensions</li>
                <li>Basic conversion tracking</li>
                <li>Landing page review or simple landing recommendation</li>
                <li>Minimum 2 month commitment</li>
              </ul>
            </article>

            <article className="google-ads-pricing-card">
              <h3>Growth</h3>
              <p className="google-ads-pricing-card__price">From $600/month</p>
              <p className="google-ads-pricing-card__setup">Setup from $1,000</p>

              <ul>
                <li>Expanded Search campaign structure</li>
                <li>More keyword coverage</li>
                <li>Ongoing optimisation and testing</li>
                <li>Stronger landing page support</li>
                <li>Broader negative keyword refinement</li>
                <li>Minimum 3 month commitment</li>
              </ul>
            </article>
          </div>

          <div className="google-ads-budget-note">
            <h3>Typical Google spend</h3>
            <p>
              Most local businesses start between <strong>$15 and $50 per day</strong>,
              depending on industry competition and location. That usually works out to
              roughly <strong>$450 to $1,500 per month</strong> paid directly to Google.
            </p>
          </div>
        </div>
      </section>

      <section className="google-ads-section google-ads-section--light">
        <div className="container google-ads-fit">
          <div className="google-ads-fit__column">
            <p className="section-kicker">Best suited to</p>
            <h2>Businesses with clear search intent</h2>
            <ul className="google-ads-checklist">
              <li>Tradies and service businesses</li>
              <li>Professionals with local service areas</li>
              <li>Businesses that want enquiries, not just traffic</li>
              <li>Owners ready to invest for at least 2 to 3 months</li>
            </ul>
          </div>

          <div className="google-ads-fit__column">
            <p className="section-kicker">Real expectations</p>
            <h2>What the first few months look like</h2>
            <div className="google-ads-timeline">
              <div className="google-ads-timeline__item">
                <span>Month 1</span>
                <p>Data collection, keyword testing, early search term cleanup.</p>
              </div>
              <div className="google-ads-timeline__item">
                <span>Month 2</span>
                <p>Refinement, tighter traffic, clearer performance signals.</p>
              </div>
              <div className="google-ads-timeline__item">
                <span>Month 3</span>
                <p>Better stability, stronger decision-making, improved lead quality.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="google-ads-section google-ads-section--alt">
        <div className="container">
          <div className="google-ads-section__intro">
            <p className="section-kicker">Why 2merative</p>
            <h2>Most providers manage ads only. We think beyond the click.</h2>
            <p>
              The ad is only one part of the result. We also look at the page, the offer,
              the call to action, and how the user actually moves toward enquiry.
            </p>
          </div>

          <div className="google-ads-difference">
            <article className="google-ads-difference__card">
              <h3>Clearer pages</h3>
              <p>
                If the landing page is working against the ad, we will tell you straight.
                Better conversion usually comes from clarity, not complexity.
              </p>
            </article>

            <article className="google-ads-difference__card">
              <h3>Local business focus</h3>
              <p>
                We build for service-based businesses that need real leads, not inflated
                vanity metrics.
              </p>
            </article>

            <article className="google-ads-difference__card">
              <h3>Practical management</h3>
              <p>
                Clean setup. Tight targeting. Useful reporting. No bloated retainers and
                no vague marketing jargon.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="google-ads-section">
        <div className="container google-ads-faq">
          <div className="google-ads-section__intro">
            <p className="section-kicker">Questions</p>
            <h2>Frequently asked questions</h2>
          </div>

          <div className="google-ads-faq__list">
            {faqs.map((item, index) => {
              const isOpen = openFaq === index

              return (
                <article className={`google-ads-faq__item ${isOpen ? "is-open" : ""}`} key={item.question}>
                  <button
                    type="button"
                    className="google-ads-faq__button"
                    onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    aria-expanded={isOpen}
                  >
                    <span>{item.question}</span>
                    <span className="google-ads-faq__icon">{isOpen ? "−" : "+"}</span>
                  </button>

                  {isOpen && <p className="google-ads-faq__answer">{item.answer}</p>}
                </article>
              )
            })}
          </div>
        </div>
      </section>

      <section id="google-ads-enquiry" className="google-ads-section google-ads-section--cta">
        <div className="container google-ads-enquiry">
          <div className="google-ads-enquiry__content">
            <p className="section-kicker">Free review</p>
            <h2>Want to know if Google Ads is a good fit for your business?</h2>
            <p>
              Send through your website and a few details. We will review the opportunity
              and let you know what we would target, what budget range makes sense, and
              whether your current site is ready.
            </p>

            <ul className="google-ads-checklist">
              <li>Free initial review</li>
              <li>Clear advice on fit and budget</li>
              <li>Honest feedback on your website or landing page</li>
            </ul>
          </div>

          <div className="google-ads-enquiry__form-wrap">
            {submitted ? (
              <div className="google-ads-success">
                <h3>Thanks. Your request has been received.</h3>
                <p>
                  We will review your website and come back with a practical recommendation.
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setSubmitted(false)}
                >
                  Submit another enquiry
                </button>
              </div>
            ) : (
              <form className="google-ads-form" onSubmit={handleSubmit}>
                <div className="google-ads-form__grid">
                  <label>
                    Name
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </label>

                  <label>
                    Business name
                    <input
                      type="text"
                      name="business"
                      value={form.business}
                      onChange={handleChange}
                      required
                    />
                  </label>

                  <label>
                    Email
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </label>

                  <label>
                    Phone
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      required
                    />
                  </label>

                  <label className="google-ads-form__full">
                    Website
                    <input
                      type="text"
                      name="website"
                      placeholder="www.yourwebsite.com.au"
                      value={form.website}
                      onChange={handleChange}
                      required
                    />
                  </label>

                  <label className="google-ads-form__full">
                    Monthly ad budget
                    <select
                      name="monthlyBudget"
                      value={form.monthlyBudget}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select budget range</option>
                      {budgetOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="google-ads-form__full">
                    What do you want more of?
                    <textarea
                      name="message"
                      rows="5"
                      placeholder="More calls, more quote requests, more bookings, better quality enquiries..."
                      value={form.message}
                      onChange={handleChange}
                      required
                    />
                  </label>
                </div>

                {error && <p className="google-ads-form__error">{error}</p>}

                <button
                  type="submit"
                  className="btn btn-primary google-ads-form__submit"
                  disabled={submitting}
                >
                  {submitting ? "Sending..." : "Request My Free Ads Review"}
                </button>

                <p className="google-ads-form__note">
                  By submitting this form, you agree to be contacted about your enquiry.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="google-ads-footer-note">
        <div className="container">
          <p>© {currentYear} 2merative Web Design</p>
        </div>
      </section>
    </main>
  )
}